import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Check } from "@phosphor-icons/react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { kfContentIn } from "@/lib/emotionTw";
import { BfrsPortalScope } from "@/components/ui/providers/BfrsProvider";
import { floatingCollisionPadding } from "@/components/ui/overlays/overlayPositioning";
import type { ActionMenuProps } from "./ActionMenu.types";

type ActionMenuSize = "xs" | "sm" | "md" | "lg";

/* Exact compiled output of the pre-migration Tailwind utilities, in the same
 * class-list emission order. Verified against the frozen baseline. */

const actionMenuItemSize: Record<ActionMenuSize, CSSObject> = {
  xs: { paddingLeft: ".625rem", paddingRight: ".625rem", paddingTop: ".375rem", paddingBottom: ".375rem", fontSize: "12px" },
  sm: { paddingLeft: ".75rem", paddingRight: ".75rem", paddingTop: ".5rem", paddingBottom: ".5rem", fontSize: ".75rem", lineHeight: "1rem" },
  md: { paddingLeft: ".875rem", paddingRight: ".875rem", paddingTop: ".625rem", paddingBottom: ".625rem", fontSize: ".875rem", lineHeight: "1.25rem" },
  lg: { paddingLeft: "1rem", paddingRight: "1rem", paddingTop: ".75rem", paddingBottom: ".75rem", fontSize: ".875rem", lineHeight: "1.25rem" }
};

const ActionMenuContent = styled(DropdownMenu.Content, {
  label: "AgenticActionMenu-content"
})({
  zIndex: 1000,
  minWidth: "12rem",
  borderRadius: "var(--bfrs-radius-lg)",
  borderWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
  padding: ".25rem",
  "--tw-shadow": "var(--bfrs-shadow-elevated)",
  "--tw-shadow-colored": "var(--bfrs-shadow-elevated)",
  boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)",
  ...kfContentIn,
  animation: "bfrs-bfrs-content-in var(--bfrs-motion-normal) var(--bfrs-ease-out)",
  maxHeight: "min(24rem,var(--radix-dropdown-menu-content-available-height))",
  overflow: "auto"
});

const ActionMenuSeparator = styled(DropdownMenu.Separator, {
  label: "AgenticActionMenu-separator"
})({
  marginTop: ".25rem",
  marginBottom: ".25rem",
  height: "1px",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-border) / var(--tw-bg-opacity, 1))"
});

const ActionMenuSectionLabel = styled(DropdownMenu.Label, {
  label: "AgenticActionMenu-sectionLabel"
})({
  paddingLeft: ".75rem",
  paddingRight: ".75rem",
  paddingTop: ".375rem",
  paddingBottom: ".375rem",
  fontSize: "10px",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: ".08em",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

interface ActionMenuItemOwnerState {
  size: ActionMenuSize;
  destructive: boolean;
}

const ActionMenuItem = styled(DropdownMenu.Item, {
  label: "AgenticActionMenu-item",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: ActionMenuItemOwnerState }>(({ ownerState }) => [
  {
    display: "flex",
    cursor: "pointer",
    WebkitUserSelect: "none",
    MozUserSelect: "none",
    userSelect: "none",
    alignItems: "center",
    gap: ".5rem",
    borderRadius: "var(--bfrs-radius-md)",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-secondary) / var(--tw-text-opacity, 1))",
    outline: "2px solid transparent",
    outlineOffset: "2px",
    "&[data-disabled]": {
      pointerEvents: "none",
      cursor: "default",
      "--tw-text-opacity": "1",
      color: "hsl(var(--bfrs-color-text-disabled) / var(--tw-text-opacity, 1))"
    },
    "&[data-highlighted]": {
      "--tw-bg-opacity": "1",
      backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))"
    }
  },
  actionMenuItemSize[ownerState.size],
  ownerState.destructive && {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-danger) / var(--tw-text-opacity, 1))"
  }
]);

const ActionMenuItemIcon = styled("span", {
  label: "AgenticActionMenu-itemIcon"
})({
  display: "flex",
  width: "1rem",
  height: "1rem",
  flexShrink: 0,
  alignItems: "center",
  justifyContent: "center"
});

const ActionMenuItemLabel = styled("span", {
  label: "AgenticActionMenu-itemLabel"
})({
  minWidth: "0px",
  flex: "1 1 0%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
});

const ActionMenuItemBadge = styled("span", {
  label: "AgenticActionMenu-itemBadge"
})({
  marginLeft: ".5rem",
  flexShrink: 0
});

const ActionMenuItemShortcut = styled("span", {
  label: "AgenticActionMenu-itemShortcut"
})({
  marginLeft: ".75rem",
  flexShrink: 0,
  fontSize: ".75rem",
  lineHeight: "1rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

export function ActionMenu({ trigger, items, align = "end", side = "bottom", size = "md", className }: ActionMenuProps) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <BfrsPortalScope>
          <ActionMenuContent align={align} side={side} sideOffset={6} collisionPadding={floatingCollisionPadding} className={className}>
            {items.map((item, index) => {
              if (item.divider) return <ActionMenuSeparator key={item.id ?? `divider-${index}`} />;
              if (item.section) {
                return <ActionMenuSectionLabel key={item.id ?? `section-${index}`}>{item.section}</ActionMenuSectionLabel>;
              }

              return (
                <ActionMenuItem
                  key={item.id ?? index}
                  disabled={item.disabled}
                  onSelect={item.onSelect}
                  ownerState={{ size, destructive: Boolean(item.destructive) }}
                >
                  <ActionMenuItemIcon>{item.checked ? <Check size={14} weight="bold" /> : item.icon}</ActionMenuItemIcon>
                  <ActionMenuItemLabel>{item.label}</ActionMenuItemLabel>
                  {item.badge && <ActionMenuItemBadge>{item.badge}</ActionMenuItemBadge>}
                  {item.shortcut && <ActionMenuItemShortcut>{item.shortcut}</ActionMenuItemShortcut>}
                </ActionMenuItem>
              );
            })}
          </ActionMenuContent>
        </BfrsPortalScope>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
